<template>
  <div class="p-1">
    <b-button
      variant="link"
      class="px-0 mb-1 text-body"
      @click="$emit('back')"
    >
      <feather-icon
        icon="ArrowLeftIcon"
        class="mr-25"
      />
      Template Management
    </b-button>

    <template v-if="isView">
      <div class="d-flex align-items-center flex-wrap mb-25">
        <h3 class="mb-0 mr-50">
          {{ form.name || '—' }}
        </h3>
        <b-badge
          v-if="form.is_default"
          variant="light-secondary"
        >
          Default
        </b-badge>
      </div>
      <a
        v-if="trackingHref"
        :href="trackingHref"
        target="_blank"
        rel="noopener noreferrer"
        class="d-inline-block text-muted mb-2"
      >
        {{ trackingLinkLabel }}
      </a>
      <p
        v-else
        class="text-muted mb-2"
      >
        —
      </p>

      <h5 class="mb-1">
        Template Image
      </h5>
      <div class="template-image-frame mb-1">
        <img
          v-if="imagePreview"
          :src="imagePreview"
          alt="Template image"
          class="template-image-banner"
        >
        <div
          v-else
          class="template-image-placeholder"
        >
          Template image 1200x400
        </div>
      </div>
      <div
        v-if="canUpdateImage"
        class="mb-2"
      >
        <b-form-group
          label-for="view-template-image"
          class="mb-50"
        >
          <template #label>
            <span class="d-inline-flex align-items-center">
              Change Image
              <feather-icon
                v-b-tooltip.hover.v-primary="'The following validations should apply when uploading image: file type must be either PNG or JPEG, file size cannot exceed 5MB, and image resolution must be 1380 pixels wide by 920 pixels high.'"
                icon="HelpCircleIcon"
                size="15"
                class="cursor-pointer text-primary ml-50"
              />
            </span>
          </template>
          <b-form-file
            id="view-template-image"
            v-model="form.image"
            accept="image/*"
            placeholder="No file chosen"
            drop-placeholder="Drop file here..."
            :disabled="imageSaving"
            @input="onSelectImage"
          />
          <small class="text-muted d-block mt-25">
            PNG or JPEG - max 5MB - 1380 x 920px.
          </small>
        </b-form-group>
        <div class="d-flex justify-content-end">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="custom-button-color"
            :disabled="!canSaveImage || imageSaving"
            @click="updateTemplateImage"
          >
            Update Image
          </b-button>
        </div>
      </div>

      <h5 class="mb-1">
        Customer Tracking Statuses
      </h5>
      <b-table
        hover
        responsive
        show-empty
        :items="form.statuses"
        :fields="viewStatusFields"
        class="hide-vertical-scroll"
      >
        <template #empty>
          <TableDataFetching
            :rows="form.statuses"
            :data-loading="loading"
          />
        </template>
        <template #cell(system_status)="data">
          <span class="text-uppercase font-weight-bolder">{{ data.value }}</span>
        </template>
        <template #cell(display_name)="data">
          {{ data.item.is_visible && data.value ? data.value : '—' }}
        </template>
        <template #cell(is_visible)="data">
          <b-badge :variant="data.value ? 'light-success' : 'light-danger'">
            {{ data.value ? 'Visible' : 'Hidden' }}
          </b-badge>
        </template>
      </b-table>
    </template>

    <template v-else>
      <h4 class="mb-25">
        Create New Template
      </h4>
      <p class="text-muted">
        Configure the tracking page merchants assigned to this template will show their customers.
      </p>
      <validation-observer
        ref="templateForm"
        v-slot="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="required"
                label="Template Name"
                label-for="template-name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Template Name"
                  rules="required|max:100"
                >
                  <b-form-input
                    id="template-name"
                    v-model="form.name"
                    placeholder="Enter template name"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label-for="template-image"
              >
                <template #label>
                  <span class="d-inline-flex align-items-center">
                    Tracking Page Image
                    <feather-icon
                      v-b-tooltip.hover.v-primary="'The following validations should apply when uploading image: file type must be either PNG or JPEG, file size cannot exceed 5MB, and image resolution must be 1380 pixels wide by 920 pixels high.'"
                      icon="HelpCircleIcon"
                      size="15"
                      class="cursor-pointer text-primary ml-50"
                    />
                  </span>
                </template>
                <b-form-file
                  id="template-image"
                  v-model="form.image"
                  accept="image/*"
                  placeholder="No file chosen"
                  drop-placeholder="Drop file here..."
                  @input="onSelectImage"
                />
                <small class="text-muted d-block mt-25">
                  PNG or JPEG - max 5MB - 1380 x 920px.
                </small>
                <small
                  v-if="imageError"
                  class="text-danger d-block"
                >{{ imageError }}</small>
                <div
                  v-if="imagePreview"
                  class="mt-1"
                >
                  <img
                    :src="imagePreview"
                    alt="Tracking page"
                    class="template-image-preview rounded"
                  >
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <h5 class="mt-2 mb-1">
            Customer Tracking Statuses
          </h5>
          <b-table
            hover
            responsive
            striped
            show-empty
            :items="form.statuses"
            :fields="statusFields"
            class="hide-vertical-scroll"
          >
            <template #empty>
              <TableDataFetching
                :rows="form.statuses"
                :data-loading="loading"
              />
            </template>
            <template #cell(system_status)="data">
              <span class="text-uppercase">{{ data.value }}</span>
            </template>
            <template #cell(display_name)="data">
              <b-form-input
                v-model="data.item.display_name"
                maxlength="100"
              />
            </template>
            <template #cell(is_visible)="data">
              <div class="d-flex justify-content-center">
                <b-form-checkbox
                  v-model="data.item.is_visible"
                  switch
                  class="mr-0"
                />
              </div>
            </template>
          </b-table>

          <b-row>
            <b-col class="d-flex justify-content-end mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="custom-button-color"
                :disabled="loading"
              >
                Submit
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </template>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BButton, BForm, BRow, BCol, BFormGroup, BFormInput, BFormFile, BFormCheckbox, BTable, BBadge, VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import { bucketUrl, trackingPageUrl, trackingEnv } from '@/constants/config'
import store from '@/store'
import TableDataFetching from '@/components/TableDataFetching.vue'

const TrackingSettingsRepository = RepositoryFactory.get('trackingSetting')

export default {
  name: 'TrackingPageTemplateForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    BButton,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormCheckbox,
    BTable,
    BBadge,
    TableDataFetching,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [SuccessMessage, ErrorMessage],
  props: {
    mode: {
      type: String,
      default: 'create',
    },
    templateId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      imageSaving: false,
      imageError: '',
      imagePreview: '',
      savedImagePreview: '',
      trackingPageLink: '',
      typevalidation: true,
      sizevalidation: true,
      resolutionvalidate: true,
      form: {
        name: '',
        is_default: false,
        image: null,
        statuses: [],
      },
      statusFields: [
        { key: 'system_status', label: 'System Status', thStyle: { width: '35%' } },
        { key: 'display_name', label: 'Customer Display Name', thStyle: { width: '50%' } },
        {
          key: 'is_visible', label: 'Visible to Customer', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '15%' },
        },
      ],
      viewStatusFields: [
        { key: 'system_status', label: 'System Status' },
        { key: 'display_name', label: 'Customer Display Name' },
        { key: 'is_visible', label: 'Visible' },
      ],
    }
  },
  computed: {
    isView() {
      return this.mode === 'view'
    },
    canUpdateImage() {
      return store.getters.permissions.filter(permission => permission === 'create-tracking-templates').length > 0
    },
    canSaveImage() {
      return this.form.image instanceof File
    },
    trackingHref() {
      return this.buildTrackingHref(this.trackingPageLink)
    },
    trackingLinkLabel() {
      return this.trackingHref.replace(/^https?:\/\//, '')
    },
  },
  mounted() {
    if (this.isView) {
      this.fetchTemplate()
    } else {
      this.fetchStatuses()
    }
  },
  methods: {
    buildTrackingHref(link) {
      if (!link) {
        return ''
      }
      let href = String(link).trim()
      if (!href.startsWith('http')) {
        if (href.includes('.')) {
          href = `https://${href}`
        } else {
          const base = (trackingPageUrl || '').replace(/\/$/, '')
          href = `${base}${href.startsWith('/') ? href : `/${href}`}`
        }
      }
      const infix = trackingEnv || (window.location.host.includes('.int.curfox.com') ? 'int' : '')
      if (!infix || href.includes(`tracking.${infix}.curfox.com`)) {
        return href
      }
      return href.replace(/tracking\.curfox\.com(?=[/:?]|$)/, `tracking.${infix}.curfox.com`)
    },
    resolveImageUrl(path) {
      if (!path) {
        return ''
      }
      if (path.startsWith('http')) {
        return path
      }
      return `${bucketUrl}${path}`
    },
    async fetchStatuses() {
      this.loading = true
      try {
        const { data } = await TrackingSettingsRepository.getTrackingPageTemplateStatuses()
        this.form.statuses = (data.data || []).map(status => ({
          ...status,
          is_visible: status.is_visible !== false,
        }))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async fetchTemplate() {
      this.loading = true
      try {
        const { data } = await TrackingSettingsRepository.getTrackingPageTemplate(this.templateId)
        const template = data.data || {}
        this.form.name = template.name || ''
        this.form.is_default = !!template.is_default
        this.form.image = null
        this.form.statuses = template.statuses || []
        this.imagePreview = this.resolveImageUrl(template.image)
        this.savedImagePreview = this.imagePreview
        this.trackingPageLink = template.tracking_page_link || ''
      } catch (e) {
        this.convertAndNotifyError(e)
        this.$emit('back')
      }
      this.loading = false
    },
    restoreSavedImagePreview() {
      if (this.isView) {
        this.imagePreview = this.savedImagePreview
      }
    },
    async onSelectImage(file) {
      this.imageError = ''
      if (!file || !(file instanceof File)) {
        this.restoreSavedImagePreview()
        return
      }
      const isValid = await this.validateTemplateImage(file)
      if (!isValid) {
        this.form.image = null
        this.restoreSavedImagePreview()
      }
    },
    async validateTemplateImage(file) {
      if (!file || !(file instanceof File)) {
        return true
      }

      this.typevalidation = true
      this.sizevalidation = true
      this.resolutionvalidate = true

      const allowedTypes = process.env.VUE_APP_ALLOWED_BACKGROUND_IMAGE_TYPES.split(',')
      const typeValidateData = await this.$validate.validateType(file.type, allowedTypes)
      this.typevalidation = typeValidateData.status
      if (!typeValidateData.status) {
        this.showErrorMessage(typeValidateData.message)
      }

      const allowedFileSize = parseInt(process.env.VUE_APP_MAXIMUM_FILE_SIZE, 10)
      const sizeValidateData = await this.$validate.validateSize(file.size, allowedFileSize)
      this.sizevalidation = sizeValidateData.status
      if (!sizeValidateData.status) {
        this.showErrorMessage(sizeValidateData.message)
      }

      const resolutionOk = await this.validateResolution(file)
      this.resolutionvalidate = resolutionOk

      if (this.typevalidation && this.sizevalidation && this.resolutionvalidate) {
        return true
      }

      this.typevalidation = true
      this.sizevalidation = true
      this.resolutionvalidate = true
      return false
    },
    validateResolution(file) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = () => {
          const image = new Image()
          image.onload = async () => {
            const fixWidth = 1380
            const fixHeight = 920
            const resolutionValidateData = await this.$validate.resolutionValidate(
              image.width,
              image.height,
              fixWidth,
              fixHeight,
            )
            if (!resolutionValidateData.status) {
              this.showErrorMessage(resolutionValidateData.message)
              if (!this.isView) {
                this.imagePreview = ''
              }
              resolve(false)
              return
            }
            this.imagePreview = reader.result
            resolve(true)
          }
          image.onerror = () => {
            this.showErrorMessage('File type is not valid.')
            resolve(false)
          }
          image.src = reader.result
        }
        reader.onerror = () => resolve(false)
        reader.readAsDataURL(file)
      })
    },
    notifyRequestError(e) {
      const payload = e && e.response && e.response.data
      if (e.response && e.response.status === 422 && payload && typeof payload === 'object' && !payload.message && !payload.errors) {
        const first = Object.values(payload)[0]
        const text = Array.isArray(first) ? first[0] : first
        if (text) {
          this.showErrorMessage(text)
          return
        }
      }
      this.convertAndNotifyError(e)
    },
    buildFormData() {
      const formData = new FormData()
      formData.append('name', this.form.name)
      if (this.form.image) {
        formData.append('image', this.form.image)
      }
      this.form.statuses.forEach((status, index) => {
        formData.append(`statuses[${index}][primary_status_id]`, status.primary_status_id)
        formData.append(`statuses[${index}][display_name]`, status.display_name || status.system_status || '')
        formData.append(`statuses[${index}][is_visible]`, status.is_visible ? 1 : 0)
      })
      return formData
    },
    async onSubmit() {
      if (this.isView) {
        return
      }
      if (this.form.image && this.form.image instanceof File) {
        const isValid = await this.validateTemplateImage(this.form.image)
        if (!isValid) {
          this.form.image = null
          return
        }
      }
      this.loading = true
      try {
        const res = await TrackingSettingsRepository.createTrackingPageTemplate(this.buildFormData())
        if (res.status === 201 || res.status === 200) {
          this.showSuccessMessage(res.data.message || 'Tracking page template created successfully.')
          this.$emit('back')
        }
      } catch (e) {
        this.notifyRequestError(e)
      }
      this.loading = false
    },
    async updateTemplateImage() {
      if (!this.canSaveImage) {
        return
      }
      const isValid = await this.validateTemplateImage(this.form.image)
      if (!isValid) {
        this.form.image = null
        this.restoreSavedImagePreview()
        return
      }
      const formData = new FormData()
      formData.append('image', this.form.image)
      this.imageSaving = true
      try {
        const res = await TrackingSettingsRepository.updateTrackingPageTemplateImage(this.templateId, formData)
        if (res.status === 200) {
          const template = (res.data && res.data.data) || {}
          this.form.image = null
          this.imagePreview = this.resolveImageUrl(template.image) || this.imagePreview
          this.savedImagePreview = this.imagePreview
          this.showSuccessMessage(res.data.message || 'Template image updated successfully.')
        }
      } catch (e) {
        this.notifyRequestError(e)
        this.restoreSavedImagePreview()
      }
      this.imageSaving = false
    },
  },
}
</script>

<style scoped>
.template-image-preview {
  max-width: 100%;
  max-height: 160px;
  object-fit: cover;
}
.template-image-frame {
  width: 100%;
  min-height: 180px;
  border-radius: 6px;
  overflow: hidden;
  background: repeating-linear-gradient(
    45deg,
    #f3f2f7,
    #f3f2f7 12px,
    #e9e7f0 12px,
    #e9e7f0 24px
  );
}
.template-image-banner {
  display: block;
  width: 100%;
  max-height: 280px;
  object-fit: cover;
}
.template-image-placeholder {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6e6b7b;
  font-weight: 600;
}
</style>
